"use client";
import React, { useState, useEffect } from "react";
import { Container, Box, Typography, Button } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import getBasicPlanPrice from "@/utils/getBasicPlanPrice";
import getUserRegion from "@/utils/getUserRegion";
import { useRouter } from "next/navigation";
import { useTranslation } from "react-i18next";
import NextLink from "next/link";
const PricingTable = () => {
  const router = useRouter();
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";

  const [basicPlanPrice, setBasicPlanPrice] = useState(99);
  const [currency, setCurrency] = useState("USD");
  const [userRegion, setUserRegion] = useState(null);

  const formatNumber = (num) => {
    if (!num) return num;
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  const convertToPKR = (usdAmount) => {
    return Math.round(usdAmount * 282);
  };

  const formatPrice = (amount, currency) => {
    if (currency === "PKR") {
      return `PKR ${formatNumber(amount)}${t("pricingTable.perMonth")}`;
    }
    return `$${amount}${t("pricingTable.perMonth")}`;
  };

  useEffect(() => {
    const fetchBasicPlanPrice = async () => {
      try {
        const regionData = await getUserRegion();
        setUserRegion(regionData);
        const priceData = await getBasicPlanPrice(regionData);
        if (priceData && priceData.amount !== undefined) {
          setBasicPlanPrice(priceData.amount);
          setCurrency(priceData.currency || "USD");
        }
      } catch (error) {
        console.error("PricingTable: Error fetching price:", error);
      }
    };
    fetchBasicPlanPrice();
  }, []);

  // Base USD prices for products
  const baseProducts = [
    { key: "interviewScheduling", usdPrice: 89 },
    { key: "videoInterview", usdPrice: 199 },
    { key: "candidateAssessment", usdPrice: 149 },
    { key: "antiCheating", usdPrice: 79 },
    { key: "multiLanguage", usdPrice: 59 },
    { key: "interviewAnalytics", usdPrice: 129 },
    { key: "collaborationTools", usdPrice: 49 },
  ];

  const products = baseProducts.map((product) => ({
    name: t(`pricingTable.products.${product.key}`),
    price: formatPrice(
      currency === "PKR" ? convertToPKR(product.usdPrice) : product.usdPrice,
      currency,
    ),
  }));

  return (
    <Box
      sx={{
        py: 4,
        px: { xs: 2, sm: 3, md: 4, lg: 0 },
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        direction: isArabic ? "rtl" : "ltr",
      }}
    >
      <Container maxWidth="md">
        {/* Header */}
        <Typography
          variant="h2"
          component="h2"
          sx={{
            mb: 3,
            color: "#0F172A",
            lineHeight: 1.3,
            fontFamily: "var(--font-plusjakarta)",
            fontWeight: "bold",
            fontSize: {
              xs: "24px",
              sm: "32px",
              md: "40px",
              lg: "40px",
              xl: "45px",
            },
            textAlign: { xs: "center", sm: "center", md: "center" },
            px: { xs: 1, sm: 2 },
          }}
        >
          {t("pricingTable.title1")}
          <br />
          {t("pricingTable.title2")}
          <Box
            component="span"
            sx={{
              color: "#775CE5",
              display: "inline-block",
              position: "relative",
            }}
          >
            {t("pricingTable.titleHighlight")}
            <Box
              component="span"
              sx={{
                position: "absolute",
                bottom: { xs: "-6px", sm: "-8px", md: "-10px" },
                left: 0,
                right: 0,
                height: { xs: "8px", sm: "10px", md: "12px" },
                background: "#FFB20099",
                borderRadius: "12px",
              }}
            />
          </Box>
        </Typography>

        {/* Subheading */}
        <Typography
          sx={{
            color: "#47405A",
            fontSize: { xs: "0.9rem", sm: "1rem", md: "1.2rem" },
            lineHeight: 1.6,
            mb: { xs: 4, sm: 5, md: 6 },
            maxWidth: "1100px",
            fontWeight: "400",
            mt: { xs: "3%", sm: "3.5%", md: "4%" },
            textAlign: { xs: "center", sm: "center", md: "center" },
            px: { xs: 1, sm: 2 },
            fontFamily: "var(--font-outfit)",
          }}
        >
          {t("pricingTable.subtitle")}
        </Typography>

        {/* Pricing Table */}
        <Box
          sx={{
            background: "white",
            borderRadius: { xs: "12px", sm: "14px", md: "16px" },
            boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
            overflow: "hidden",
            mb: { xs: 4, sm: 4.5, md: 5 },
            mx: { xs: 0.5, sm: 1 },
          }}
        >
          {/* Table Header */}
          <Box
            sx={{
              background: "linear-gradient(90deg, #9810FA 0%, #8200DB 100%)",
              p: { xs: "14px 16px", sm: "18px 22px", md: "27px 30px" },
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography
              color="white"
              fontWeight={600}
              fontSize={{ xs: "0.9rem", sm: "1.05rem", md: "1.3rem" }}
              sx={{ fontFamily: "var(--font-outfit)" }}
            >
              {t("pricingTable.tableHeaderSolution")}
            </Typography>
            <Typography
              color="white"
              fontWeight={600}
              fontSize={{ xs: "0.9rem", sm: "1.05rem", md: "1.3rem" }}
              sx={{ fontFamily: "var(--font-outfit)" }}
            >
              {t("pricingTable.tableHeaderWorth")}
            </Typography>
          </Box>

          {/* Product Rows */}
          {products.map((product, index) => (
            <Box
              key={index}
              sx={{
                p: { xs: "10px 14px", sm: "14px 18px", md: "20px 30px" },
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                borderBottom:
                  index < products.length - 1 ? "1px solid #F3F4F6" : "none",
                transition: "background 0.2s ease",
                cursor: "default",
                "&:hover": { background: "#FAFAFA" },
                gap: { xs: 1, sm: 2 },
              }}
            >
              <Typography
                sx={{
                  color: "#364153",
                  fontSize: { xs: "0.8rem", sm: "0.95rem", md: "1.1rem" },
                  fontFamily: "var(--font-outfit)",
                  fontWeight: "500",
                  flex: 1,
                }}
              >
                {product.name}
              </Typography>
              <Typography
                sx={{
                  color: "#1E2939",
                  fontSize: { xs: "0.75rem", sm: "0.9rem", md: "1rem" },
                  fontFamily: "var(--font-outfit)",
                  fontWeight: "600",
                  whiteSpace: "nowrap",
                }}
              >
                {product.price}
              </Typography>
            </Box>
          ))}

          {/* Total Row */}
          <Box
            sx={{
              p: { xs: "14px 16px", sm: "16px 20px", md: "20px 30px" },
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              background: "linear-gradient(90deg, #F9FAFB 0%, #FAF5FF 100%)",
              borderTop: "2px solid #E5E7EB",
              flexDirection: { xs: "column", sm: "row", md: "row" },
              gap: { xs: 1.5, sm: 0 },
            }}
          >
            <Typography
              fontSize={{ xs: "0.95rem", sm: "1.05rem", md: "1.2rem" }}
              color="#1F2937"
              sx={{
                fontFamily: "var(--font-outfit)",
                fontWeight: "bold",
                textAlign: { xs: "center", sm: isArabic ? "right" : "left" },
              }}
            >
              {t("pricingTable.totalValue")}
            </Typography>
            <Box
              display="flex"
              alignItems="center"
              gap={{ xs: "6px", sm: "10px", md: "12px" }}
              sx={{
                flexWrap: { xs: "wrap", sm: "nowrap" },
                justifyContent: { xs: "center", sm: "flex-end" },
              }}
            >
              <Typography
                sx={{
                  color: "#9810FA",
                  fontSize: { xs: "1.3rem", sm: "1.5rem", md: "1.9rem" },
                  fontWeight: "bold",
                  fontFamily: "var(--font-outfit)",
                }}
              >
                {currency === "PKR"
                  ? `PKR ${formatNumber(convertToPKR(753))}`
                  : "$753"}
              </Typography>
              <Typography
                sx={{
                  color: "#9810FA",
                  fontSize: { xs: "0.7rem", sm: "0.8rem", md: "15px" },
                  fontWeight: "bold",
                  fontFamily: "var(--font-outfit)",
                  ml: "-1%",
                }}
              >
                {t("pricingTable.perMonth")}
              </Typography>
              <Typography
                sx={{
                  background: "#FFDF20",
                  color: "#894B00",
                  fontFamily: "var(--font-outfit)",
                  px: { xs: 1.5, sm: 2 },
                  py: { xs: 0.5, sm: 1 },
                  borderRadius: "20px",
                  fontSize: { xs: "0.75rem", sm: "0.9rem", md: "1rem" },
                  fontWeight: 700,
                }}
              >
                {t("pricingTable.saveBig")}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Pricing Info */}
        <Typography
          sx={{
            color: "#1E2939",
            fontSize: { xs: "0.95rem", sm: "1.05rem", md: "1.3rem" },
            fontWeight: 600,
            mb: { xs: 2.5, sm: 3, md: 3 },
            fontFamily: "var(--font-outfit)",
            pt: { xs: "2%", sm: "2.5%", md: "3%" },
            textAlign: { xs: "center", sm: "center", md: "center" },
            px: { xs: 1, sm: 2 },
          }}
        >
          {t("pricingTable.pricingInfo", {
            currency,
            price: formatNumber(basicPlanPrice),
          })}
        </Typography>

        {/* CTA Button */}
        <Box
          sx={{
            textAlign: { xs: "center", sm: "center", md: "center" },
            px: { xs: 1, sm: 2 },
          }}
        >
          <NextLink href="/free-trial-of-ai-recruiting-tool" passHref legacyBehavior>
            <Button
              component="a"
              variant="contained"
              sx={{
                background: "#9810FA",
                color: "white",
                px: { xs: 2.5, sm: 3.5, md: 5 },
                py: { xs: 1.3, sm: 1.6, md: 2 },
                borderRadius: "50px",
                fontSize: { xs: "0.67rem", sm: "0.95rem", md: "1.1rem" },
                fontWeight: 600,
                display: "inline-flex",
                alignItems: "center",
                fontFamily: "var(--font-outfit)",
                gap: { xs: "6px", sm: "8px" },
                mt: "1%",
                textTransform: "none",
                transition: "transform 0.2s ease-in-out",
                "&:hover": {
                  transform: "scale(1.05) translateY(-2px)",
                  background: "#000",
                },
              }}
            >
              {t("pricingTable.cta")}
              {isArabic ? (
                <ChevronLeftIcon sx={{ fontSize: { xs: 20, sm: 22, md: 24 } }} />
              ) : (
                <ChevronRightIcon
                  aria-label="Navigate"
                  sx={{ fontSize: { xs: 20, sm: 22, md: 24 } }}
                />
              )}
            </Button>
          </NextLink>
        </Box>
      </Container>
    </Box>
  );
};

export default PricingTable;
